
<template>
  <woot-modal :show="show" :on-close="onClose">
    <woot-modal-header
      :header-title="isEditing ? $t('BUSINESS_DATA.FAQS.EDIT') : $t('BUSINESS_DATA.FAQS.ADD_NEW')"
    />
    <form
      class="flex flex-col modal-content"
      @submit.prevent="onSubmit"
    >
      <div class="space-y-5">
          <div class="space-y-2">
            <woot-input
              v-model="formData.question"
              :label="$t('BUSINESS_DATA.FAQS.QUESTION')"
              :required="true"
              :placeholder="$t('BUSINESS_DATA.FAQS.QUESTION_PLACEHOLDER')"
              :error="v$.formData.question.$error ? getQuestionErrorMessage() : ''"
              :class="{ error: v$.formData.question.$error }"
              type="text"
              maxlength="100"
              @blur="v$.formData.question.$touch"
            />
            <div v-if="!v$.formData.question.$error" class="flex justify-end text-xs">
              <span :class="formData.question.length > 80 ? 'text-red-500 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'">
                {{ formData.question.length }}/100
              </span>
            </div>
          </div>

          <div class="space-y-2">
            <div class="space-y-3">
              <div>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ $t('BUSINESS_DATA.FAQS.ANSWER_MODE_HINT') }}
                </p>
              </div>

              <div
                v-if="!formData.answer_steps.length"
                class="flex items-center justify-between rounded-lg border border-dashed border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-500 dark:text-slate-400"
              >
                <span>{{ $t('BUSINESS_DATA.FAQS.ANSWER_STEPS_EMPTY') }}</span>
                <woot-button
                  type="button"
                  size="tiny"
                  variant="smooth"
                  icon="add"
                  :disabled="formData.answer_steps.length >= maxSteps"
                  @click="addStep"
                >
                  {{ $t('BUSINESS_DATA.FAQS.ANSWER_STEPS_ADD') }}
                </woot-button>
              </div>

              <div
                v-for="(step, index) in formData.answer_steps"
                :key="`faq-step-${index}`"
                class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 p-3 space-y-2"
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="flex items-center gap-2">
                    <div class="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {{ index + 1 }}
                    </div>
                    <span
                      v-if="isCollapsed(index)"
                      class="text-xs text-slate-500 dark:text-slate-400 truncate text-left max-w-[55vw] sm:max-w-[320px] lg:max-w-[400px]"
                    >
                      {{ getStepPreview(step) }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <woot-button
                      v-if="formData.answer_steps.length > 1"
                      type="button"
                      size="tiny"
                      variant="clear"
                      icon="dismiss"
                      class="text-slate-500 hover:text-red-500"
                      @click="removeStep(index)"
                    />
                    <woot-button
                      type="button"
                      size="tiny"
                      variant="clear"
                      :icon="isCollapsed(index) ? 'chevron-down' : 'chevron-up'"
                      class="text-slate-500"
                      @click="toggleStep(index)"
                    />
                  </div>
                </div>

                <template v-if="!isCollapsed(index)">
                  <TextArea
                    v-model="step.message"
                    :placeholder="$t('BUSINESS_DATA.FAQS.ANSWER_STEPS_MESSAGE_PLACEHOLDER')"
                    auto-height
                    :resize="true"
                    :min-height="'60px'"
                    :max-height="'200px'"
                    custom-text-area-wrapper-class="!px-0 !pt-0 !pb-0 !border-0 !bg-transparent"
                  />
                  <div class="flex items-center justify-end text-xs text-slate-500 dark:text-slate-400">
                    <span>{{ $t('BUSINESS_DATA.FAQS.ANSWER_STEPS_IMAGE_HINT') }}</span>
                  </div>

                  <ImagePicker
                    v-model="step.image_item_ids"
                    :account-id="currentAccountId"
                  />
                </template>
              </div>

              <div
                v-if="formData.answer_steps.length"
                class="flex justify-end"
              >
                <woot-button
                  type="button"
                  size="tiny"
                  variant="hollow"
                  icon="add"
                  :disabled="formData.answer_steps.length >= maxSteps"
                  @click="addStep"
                >
                  {{ $t('BUSINESS_DATA.FAQS.ANSWER_STEPS_ADD') }}
                </woot-button>
              </div>
            </div>
            <div
              class="flex justify-end text-xs text-slate-500 dark:text-slate-400"
              v-if="formData.answer_steps.length"
            >
              <span :class="totalAnswerLength > maxTotalLength ? 'text-red-500 dark:text-red-400' : ''">
                {{ $t('BUSINESS_DATA.FAQS.ANSWER_TOTAL_COUNTER') }}: {{ totalAnswerLength }}/{{ maxTotalLength }}
              </span>
            </div>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ $t('BUSINESS_DATA.FAQS.CATEGORY') }}
            </label>
            <CategorySelector
              v-model="formData.business_category_id"
              :categories="businessCategories"
              category-type="faq"
              :placeholder="$t('BUSINESS_DATA.FAQS.CATEGORY_PLACEHOLDER')"
              @category-created="onCategoryCreated"
              @category-updated="onCategoryUpdated"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('BUSINESS_DATA.FAQS.CATEGORY_HELP') }}
            </p>
          </div>

          <div class="space-y-2">
            <div class="flex items-center gap-3">
              <woot-switch
                :model-value="formData.is_active"
                @input="formData.is_active = $event"
              />
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ $t('BUSINESS_DATA.GENERAL.ACTIVE') }}
              </span>
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('BUSINESS_DATA.FAQS.ACTIVE_HELP') }}
            </p>
          </div>
      </div>

      <div class="flex justify-center gap-3 pt-4">
        <woot-button
          type="button"
          variant="smooth"
          color-scheme="ghost"
          @click="onClose"
        >
          {{ $t('COMMON.CANCEL') }}
        </woot-button>

        <woot-button
          type="submit"
          variant="smooth"
          color-scheme="success"
          :is-loading="isSaving"
          :disabled="!isFormValid"
        >
          {{ isEditing ? $t('COMMON.UPDATE') : $t('COMMON.CREATE') }}
        </woot-button>
      </div>
    </form>
  </woot-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ImagePicker from './ImagePicker.vue';
import CategorySelector from './CategorySelector.vue';

export default {
  name: 'FaqModal',
  components: { ImagePicker, CategorySelector, TextArea },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    faq: {
      type: Object,
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    businessCategories: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'saved', 'category-created'],
  setup() {
    return { v$: useVuelidate() };
  },
  
  data() {
    return {
      isSaving: false,
      formData: {
        question: '',
        business_category_id: null,
        is_active: true,
        answer_steps: [],
      },
      maxSteps: 20,
      maxTotalLength: 2000,
      collapsedSteps: [],
    };
  },
  
  computed: {
    isEditing() {
      return !!this.faq;
    },
    
    isFormValid() {
      const hasSteps = this.formData.answer_steps.length > 0;
      return (
        this.formData.question.trim() &&
        this.formData.question.length <= 100 &&
        this.isTotalLengthValid &&
        hasSteps
      );
    },

    totalAnswerLength() {
      return this.formData.answer_steps.reduce(
        (sum, step) => {
          const message = step.message || '';
          return sum + message.trim().length;
        },
        0
      );
    },

    isTotalLengthValid() {
      return this.totalAnswerLength <= this.maxTotalLength;
    },

    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },
  },
  validations: {
    formData: {
      question: { required, maxLength: maxLength(100) }
    },
  },
  
  watch: {
    faq: {
      immediate: true,
      handler(newFaq) {
        if (newFaq) {
          this.formData = {
            question: newFaq.question || '',
            business_category_id: newFaq.business_category?.id || null,
            is_active: newFaq.is_active !== undefined ? newFaq.is_active : true,
            answer_steps: this.normalizeSteps(newFaq.answer_steps),
          };
          this.collapsedSteps = [];
        } else {
          this.formData = {
            question: '',
            business_category_id: null,
            is_active: true,
            answer_steps: [],
          };
          this.collapsedSteps = [];
        }
      },
    },
    
    show(newShow) {
      if (!newShow) {
        this.resetForm();
      }
    },
    
    businessCategories: {
      immediate: true,
    },
  },
  
  methods: {
    addStep() {
      if (this.formData.answer_steps.length >= this.maxSteps) return;
      // Collapse existing steps when adding a new one
      this.collapsedSteps = this.formData.answer_steps.map((_s, idx) => idx);
      this.formData.answer_steps = [
        ...this.formData.answer_steps,
        { message: '', image_item_ids: [] },
      ];
    },

    removeStep(index) {
      this.formData.answer_steps = this.formData.answer_steps.filter(
        (_step, idx) => idx !== index
      );
      this.collapsedSteps = this.collapsedSteps
        .filter(i => i !== index)
        .map(i => (i > index ? i - 1 : i));
    },

    normalizeSteps(rawSteps) {
      const steps = Array.isArray(rawSteps) ? rawSteps : [];
      return steps.map(step => {
        if (typeof step === 'string') {
          return { message: step, image_item_ids: [] };
        }
        return {
          message: step?.message || '',
          image_item_ids: Array.isArray(step?.image_item_ids)
            ? step.image_item_ids
            : [],
        };
      });
    },

    cleanSteps(steps) {
      return (steps || [])
        .filter(step => (step.message || '').trim() || (step.image_item_ids || []).length)
        .map(step => ({
          message: (step.message || '').trim(),
          image_item_ids: (step.image_item_ids || []).filter(id => !!id),
        }));
    },

    resetForm() {
      this.formData = {
        question: '',
        business_category_id: null,
        is_active: true,
        answer_steps: [],
      };
      this.v$.$reset();
      this.collapsedSteps = [];
    },
    
    onCategoryCreated(newCategory) {
      // Auto select the newly created category
      this.formData.business_category_id = newCategory.id;
      this.$emit('category-created', newCategory);
    },

    onCategoryUpdated() {
      this.$emit('category-updated');
    },
    
    getQuestionErrorMessage() {
      return this.$t('BUSINESS_DATA.FAQS.QUESTION_ERROR');
    },

    toggleStep(index) {
      if (this.collapsedSteps.includes(index)) {
        this.collapsedSteps = this.collapsedSteps.filter(i => i !== index);
      } else {
        this.collapsedSteps = [...this.collapsedSteps, index];
      }
    },

    getStepPreview(step) {
      const message = (step.message || '').trim();
      if (!message && !(step.image_item_ids || []).length) return this.$t('BUSINESS_DATA.FAQS.ANSWER_STEPS_EMPTY');
      if (!message) return this.$t('BUSINESS_DATA.FAQS.ANSWER_STEPS_IMAGE_ONLY');
      return message.length > 150 ? `${message.slice(0, 150)}…` : message;
    },

    isCollapsed(index) {
      return this.collapsedSteps.includes(index);
    },
    
    async onSubmit() {
      if (!this.isFormValid || this.isSaving) {
        return;
      }
      
      this.isSaving = true;
      try {
        const payload = {
          question: (this.formData.question || '').trim(),
          business_category_id: this.formData.business_category_id,
          is_active: this.formData.is_active,
          answer_steps: this.cleanSteps(this.formData.answer_steps),
        };
        
        if (this.isEditing) {
          await window.axios.put(
            `/api/v1/accounts/${this.currentAccountId}/business_faqs/${this.faq.id}`,
            payload
          );
        } else {
          await window.axios.post(
            `/api/v1/accounts/${this.currentAccountId}/business_faqs`,
            payload
          );
        }
        
        this.$emit('saved');
        this.resetForm();
      } catch (error) {
        console.error('Error saving FAQ:', error);
        console.error('Error response:', error?.response?.data);
        
        let errorMessage = this.$t('BUSINESS_DATA.ERROR.SAVE_FAILED');
        
        if (error?.response?.data?.errors) {
          errorMessage = error.response.data.errors.join(', ');
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        useAlert(errorMessage, { type: 'error' });
      } finally {
        this.isSaving = false;
      }
    },
    
    onClose() {
      this.resetForm();
      this.$emit('close');
    },

  },
};
</script>
